import { accountState } from "@/atoms/accountState";

import Dialog from "@/components/Dialog/Dialog";

import { Drawer, DrawerContent } from "@chakra-ui/react";
import styled from "@emotion/styled";
import {
  colors,
  VStack,
  HStack,
  ColorIcon,
  Button,
  Spinner,
  customEvent,
  call,
  queryKeys,
} from "goi_common";
import { useRouter } from "next/router";
import {
  useCallback,
  useEffect,
  useState,
  // useMemo
} from "react";
import { useQuery, useQueryClient } from "react-query";
import { useRecoilValueLoadable } from "recoil";
import { getQuestion } from "../remotes/getQuestion";
import { postQuestion } from "../remotes/postQuestion";
import { putQuestion } from "../remotes/putQuestion";

interface QuestionDrawerProps {
  id?: number;
  isOpen: boolean;
  onClose: () => void;
}
export function QuestionDrawer({ id, isOpen, onClose }: QuestionDrawerProps) {
  const router = useRouter();
  const queryClient = useQueryClient();

  const [content, setContent] = useState<string>("");
  const account = useRecoilValueLoadable(accountState);
  const nickname = account.state === "hasValue" && account.contents?.nickname;

  const [loading, setLoading] = useState<boolean>(false);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);

  const { data: question, isLoading } = useQuery(queryKeys.question(id), () => getQuestion(id!), {
    enabled: id != null && id >= 0,
  });

  useEffect(() => {
    if (!question) return;
    if (!question.is_users) {
      alert("자신이 작성한 글만 수정할 수 있습니다.");
      onClose();
      return;
    }

    setContent(question.content);

    return () => {
      setContent("");
    };
  }, [onClose, question]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        const input = document.getElementById("question-input");
        input?.blur();
      }, 100);
    }
  }, [isOpen]);

  const handleSubmit = useCallback(async () => {
    customEvent({
      newGtm: {
        path: "guidebook",
        trigger_and_target: "post_qna_modal-post_question",
      },
    });
    try {
      setLoading(true);
      const { id } = question
        ? await putQuestion(question.id, content)
        : await postQuestion(content);
      await queryClient.invalidateQueries("questions");
      await router.push({ pathname: "/guidebook", query: { tab: "qna", id } });
      setContent("");
    } catch (e: any) {
      if (e.response && e.response.status === 400) {
        setDialogOpen(true);
      } else {
        console.log(e);
      }
    } finally {
      setLoading(false);
    }
  }, [content, queryClient, question]);

  return (
    <>
      <Drawer isOpen={isOpen} onClose={onClose} placement="bottom">
        <DrawerContent
          width="100% !important"
          height="100%"
          boxShadow="none"
          marginLeft="var(--left)"
          overflow="auto"
          alignItems="center"
        >
          <VStack maxWidth="1200px" width="100%">
            <CloseHeader>
              <VStack onClick={onClose} style={{ cursor: "pointer" }}>
                <ColorIcon name="icon-x-mono" color={colors.gray900} size={40} />
              </VStack>
            </CloseHeader>

            {!isLoading && (
              <VStack gap={20} padding="24px">
                <VStack gap={36}>
                  <VStack gap={12}>
                    <div className="head2_m black">
                      <span className="orange650">{nickname}</span>님.
                      <br />
                      성심성의껏 답변해 드리겠습니다.
                    </div>
                    <HStack
                      className="body2 gray700"
                      alignItems="center"
                      onClick={() => {
                        customEvent({
                          newGtm: {
                            path: "guidebook",
                            trigger_and_target: "want_call-call",
                            text: "1:1 전화 상담을 원한다면",
                          },
                        });
                        call("1666-9784");
                      }}
                      width="fit-content"
                      style={{ cursor: "pointer" }}
                    >
                      <ColorIcon name="headphone" color={colors.gray700} size={32} />
                      <span style={{ margin: "0 6px 0 2px " }}>1:1 전화 상담을 원한다면</span>
                      <ColorIcon
                        name="icon-arrow-right-small-mono"
                        color={colors.gray700}
                        size={16}
                      />
                    </HStack>
                  </VStack>

                  <VStack gap={18}>
                    <VStack gap={12} alignItems="flex-end">
                      <VStack width="100%" position="relative">
                        <Textarea
                          id="question-input"
                          className="body2 gray900"
                          value={content}
                          onChange={(e) => setContent(e.target.value)}
                          placeholder="궁금한 점을 입력하세요."
                          maxLength={300}
                        />
                        <div
                          css={{
                            bottom: 0,
                            right: 0,
                            zIndex: 3,
                            width: "30px",
                            height: "30px",
                            position: "absolute",
                          }}
                          onClick={() => setContent("")}
                        >
                          <ClearIcon color="" name="clear" size={30} />
                        </div>
                      </VStack>

                      <div className="caption1 gray600">{content.length}자 / 300자</div>
                    </VStack>

                    <div className="body3_r gray500">
                      운영시간 : 월~금 09:00 ~ 18:00
                      <br />
                      문의량이 많을 경우 답변이 지연될 수 있습니다.
                    </div>
                  </VStack>
                </VStack>

                <Button
                  css={{
                    width: "100%",
                    height: "62px",
                    backgroundColor: colors.black,
                    color: colors.white,
                  }}
                  disabled={content.length === 0}
                  onClick={handleSubmit}
                >
                  {loading && <Spinner />}
                  <span className="subtitle2_b">질문 남기기</span>
                </Button>
              </VStack>
            )}
          </VStack>
        </DrawerContent>
      </Drawer>
      <Dialog
        type={1}
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        header="수정할 수 없습니다."
        footerProps={{
          text: "확인",
          onClick: () => setDialogOpen(false),
        }}
      >
        답변이 달린 질문은 수정할 수 없습니다.
      </Dialog>
    </>
  );
}

const CloseHeader = styled(HStack)`
  justify-content: flex-end;
  padding: 11px 24px;
  position: sticky;
  top: 0;
  background: var(--white);
`;
const Textarea = styled.textarea`
  width: 100%;
  height: 252px;
  background: var(--gray50);
  border-radius: 8px;
  outline: none;
  resize: none;
  padding: 16px 20px;
  ::placeholder {
    color: var(--gray500);
  }
`;
const ClearIcon = styled(ColorIcon)`
  position: absolute;
  bottom: 6px;
  right: 8px;
  cursor: pointer;
`;
